import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useLanguageHook } from "../../../../../../../../hooks/useLanguageHook";
import { Languages as LanguagesType } from "../../../../../../../../models/languages";
import { Status } from "../../../../../../../../util/enums";
import { ActionButtonProps } from "../../../../../../../../components/Generic/ActionButtons";
import Modal, { StatusButtonProps } from "../../../../../../../../components/Generic/Modal";
import UISteps, { Step } from "../../../../../../../../components/Generic/UISteps";
import {
  TypeOfButton,
  useStepsAction,
} from "../../../../../../../../components/Generic/UISteps/useActions";
import LanguageForm from "./LanguageForm";
import useStyles from "./styles";
import TranslationForm from "./TranslationForm";

enum StepType {
  GENERAL = "General",
  TRANSLATION = "Translation",
}

interface Props {
  languageToUpdate?: LanguagesType;
  open: boolean;
  hasError?: boolean;
  isLoading: boolean;
  closeModal: () => void;
  updateLanguage?: (language: LanguagesType) => void;
  createLanguage?: (language: LanguagesType) => void;
}

const LanguageModal = ({
  updateLanguage,
  createLanguage,
  open,
  hasError,
  isLoading,
  closeModal,
}: Props) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  const { language, handleUpdateLanguage } = useLanguageHook();

  const { setButton, onCancel, onFinish } = useStepsAction();

  const [currentStep, setCurrentStep] = useState<string>(StepType.GENERAL);
  const [options, setOptions] = useState<string[]>([StepType.GENERAL]);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const generalStep: Step = {
    label: translate("general"),
    key: StepType.GENERAL,
    component: (
      <div className={classes.createFormWrapper}>
        <LanguageForm errors={errors} isLoading={false} isOnEditModal />
      </div>
    ),
  };

  const translationStep: Step = {
    label: translate("translation"),
    key: StepType.TRANSLATION,
    component: <TranslationForm />,
  };

  const steps: Step[] = [generalStep, translationStep];

  const schema = yup.object().shape({
    baseLanguageId: yup
      .number()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (baseLanguageId) =>
        baseLanguageId ? !Number.isNaN(baseLanguageId) : false
      ),
    languageName: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (languageName) =>
        languageName ? languageName !== "" : false
      ),
    languagePrefix: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (languagePrefix) =>
        languagePrefix ? languagePrefix !== "" : false
      ),
    languageFlag: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (languageFlag) =>
        languageFlag ? languageFlag !== "" : false
      ),
    languageStatus: yup.string().typeError(translate("thisFieldIsRequired")),
    languageUsage: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired"))
      .test("empty-check", translate("thisFieldIsRequired"), (languageUsage) =>
        languageUsage ? languageUsage !== "" : false
      ),
  });

  const isValid = () => {
    try {
      schema.validateSync(
        {
          languageName: language?.name,
          languagePrefix: language?.prefix,
          languageFlag: language?.flag,
          languageStatus: language?.status,
          languageUsage: language?.usage,
          baseLanguageId: language?.baseLanguageId,
        },
        { abortEarly: false }
      );
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrors(error?.inner);

      return false;
    }
  };

  const handleSubmit = () => {
    if (language && isValid()) {
      if (language?.id && updateLanguage) {
        onFinish(isValid(), false, options, steps, () => updateLanguage(language), nextStep);
      } else if (createLanguage) {
        createLanguage(language);
      }
    }
  };

  const setStatus = (): string => {
    return language?.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE;
  };

  const nextStep = (step: string) => {
    setOptions((state) => (state ? [...state, step] : [step]));
    setCurrentStep(step);
  };

  const previousStep = (nextOptions: string[], nextStep: string) => {
    setOptions([...nextOptions]);
    setCurrentStep(nextStep);
  };

  const handleSetButton = (button: string, toUpdate?: boolean) => {
    const { text, color } = setButton({
      type: button,
      isToUpdate: toUpdate ?? false,
      options,
      steps,
    });

    return { text, color };
  };

  const actionButtonProps: ActionButtonProps = {
    textSave: handleSetButton(TypeOfButton.SUBMIT, currentStep === StepType.TRANSLATION).text,
    textCancel: handleSetButton(TypeOfButton.CANCEL, false).text,
    customColorCancel: handleSetButton(TypeOfButton.CANCEL, false).color,
    customColorSave: handleSetButton(TypeOfButton.SUBMIT).color,
    loading: isLoading,
    onSave: handleSubmit,
    onCancel: () => onCancel(options, false, closeModal, previousStep),
  };

  const statusButtonProps: StatusButtonProps = {
    status: language?.status || Status.INACTIVE,
    action: () => handleUpdateLanguage("status", setStatus()),
  };

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      closeModal();
    }
  }, [hasError]);

  return (
    <Modal
      open={open}
      handleClose={() => {
        closeModal();
      }}
      title={language?.id ? translate("editLanguage") : translate("createNewLanguage")}
      showActionButtons
      actionButtonProps={
        language?.id
          ? actionButtonProps
          : {
              onSave: handleSubmit,
              onCancel: () => {
                closeModal();
              },
              textSave: language?.id ? translate("update") : translate("create"),
              dataCySave: "submitChanges",
              dataCyCancel: "cancelButton",
              loading: isLoading,
              saveDisabled: isLoading,
            }
      }
      showButtonStatus={!!language?.id}
      statusButtonProps={statusButtonProps}
    >
      {language?.id ? (
        <UISteps steps={steps} currentStep={currentStep} stepsOption={options} />
      ) : (
        <LanguageForm isLoading={isLoading} errors={errors} />
      )}
    </Modal>
  );
};

export default LanguageModal;
